import { computed, onScopeDispose, shallowRef, watch, type Ref, type WritableComputedRef } from "vue";
import { useRoute, useRouter } from "vue-router";

import pinia from "@/plugins/PiniaPlugin";
import { useApplicationStore } from "@/stores/Application";
import { useArticlesStore } from "@/stores/Articles";
import { useAuthStore } from "@/stores/Auth";
import { useTradeAlertsStore } from "@/stores/TradeAlerts";
import { useWebinarsStore } from "@/stores/Webinars";
import { firstQueryValue } from "@/composables/useRouteQueryState";
import { isOverlayHistoryEntry, leaveOverlayHistoryEntry, overlayHistoryState } from "@/router/overlay-history";
import routeManager from "@/router/route-manager";
import { isFreelyAvailableContent } from "@/utils/access-utils";
import {
    ContentPreviewQueryKey,
    type ContentPreviewTarget,
    contentPreviewValue,
    isPreviewContentKey,
    parseContentPreview,
    withContentPreview
} from "@/utils/content-preview-query";

import type { ContentfulItem } from "@tmi/types/entities";
import { tradeAlertKey, webinarKey } from "@/types/products/product-keys";

/*
  UX roadmap #14: the content slide-over (an article, trade alert or webinar over a list, the dashboard or the bell) had
  no address. On a phone it is full-screen, and Back navigated the page underneath (or left the app) while it stayed
  on top; a preview could not be shared, refreshed or opened in a new tab.

  It now lives in the query as ?preview=<content key>:<slug>, on whatever page it opens over, public pages included.
  Opening it pushes an entry, so Back closes it; closing it goes Back from that entry, or replaces the address when the
  preview arrived by link. Only the query changes, so the page keeps its scroll and focus (router/main-scroll.ts,
  router/route-focus.ts).

  The store stays the one place that opens it (setSelectedArticle and the rest have many callers): this syncs the two
  ways. A cold ?preview= link resolves the item by slug through the same requests the full pages make, with a skeleton
  in the slide-over meanwhile. Those requests return paid items to anonymous visitors too (the pages lock them in place),
  so a paid item for a signed-out visitor is treated like one that resolves to nothing. That, and a failed request, send
  the visitor to the item's own page, which says it is locked, missing or failed and offers what to do next.
*/

/** The item's own page: where a preview that cannot be shown sends the visitor. */
export function contentPageRoute( target: ContentPreviewTarget ): string {
    switch ( target.contentKey ) {
        case tradeAlertKey:
            return routeManager.tradeAlertRouteBySlug( target.slug );
        case webinarKey:
            return routeManager.webinarRouteBySlug( target.slug );
        default:
            return routeManager.articleRouteBySlug( target.slug );
    }
}

async function fetchPreview( target: ContentPreviewTarget, signal: AbortSignal ): Promise< ContentfulItem | undefined > {
    switch ( target.contentKey ) {
        case tradeAlertKey:
            return useTradeAlertsStore( pinia ).fetchTradeAlertBySlugIfMissing( target.slug, { signal } );
        case webinarKey:
            return useWebinarsStore( pinia ).fetchWebinarBySlugIfMissing( target.slug, { signal } );
        default:
            return useArticlesStore( pinia ).fetchArticleBySlugIfMissing( target.slug, { signal } );
    }
}

async function resolvePreview( target: ContentPreviewTarget, signal: AbortSignal ): Promise< ContentfulItem | undefined > {
    if ( useAuthStore( pinia ).isAuthenticated ) {
        return fetchPreview( target, signal );
    }

    // Members only: an anonymous visitor goes to the alert's page, whose guard asks them to sign in and return
    if ( target.contentKey === tradeAlertKey ) return undefined;

    // The by-slug requests do not filter paid items out for anonymous visitors: Article.vue and Webinar.vue lock them
    // in place (UX roadmap #80). Opening one here would show its full body in the slide-over, so its page shows the lock.
    const item: ContentfulItem | undefined = await fetchPreview( target, signal );
    return isFreelyAvailableContent( item?.products.map( p => p.slug ) ) ? item : undefined;
}

export interface ContentPreviewRoute {
    /** The slide-over's v-model: open while an item is shown or a ?preview= link is resolving; false closes it. */
    drawerVisible: WritableComputedRef< boolean >;
    /** True while a ?preview= link is being resolved (the slide-over shows a skeleton). */
    isResolving: Readonly< Ref< boolean > >;
}

/** Call once, from the component that renders the slide-over. */
export function useContentPreviewRoute(): ContentPreviewRoute {
    const route = useRoute();
    const router = useRouter();
    const appStore = useApplicationStore( pinia );

    /** The preview the address names, normalised. */
    const addressed = computed< string | undefined >( () => {
        const target: ContentPreviewTarget | null = parseContentPreview( firstQueryValue( route.query[ ContentPreviewQueryKey ] ) );
        return target ? contentPreviewValue( target.contentKey, target.slug ) : undefined;
    } );

    /** The preview on screen, when it has an address (an item without a slug, or a bonus report, has none). */
    const shown = computed< string | undefined >( () => {
        const contentKey: string | undefined = appStore.contentKey;
        const slug: string | undefined = ( appStore.contentItem as ContentfulItem | undefined )?.slug;
        return appStore.contentModalVisible && isPreviewContentKey( contentKey ) && slug
            ? contentPreviewValue( contentKey!, slug )
            : undefined;
    } );

    // The ?preview= value being resolved, and the request resolving it.
    const resolving = shallowRef< string | undefined >();
    let request: AbortController | undefined;

    function cancelResolve(): void {
        request?.abort();
        request = undefined;
        resolving.value = undefined;
    }

    async function openFromAddress( value: string ): Promise< void > {
        const target: ContentPreviewTarget = parseContentPreview( value )!;
        const own = new AbortController();
        request = own;
        resolving.value = value;

        let item: ContentfulItem | undefined;
        let failed: boolean = false;
        try {
            item = await resolvePreview( target, own.signal );
        } catch {
            // The store has reported it; the item's page shows its own "couldn't load" with a retry
            failed = true;
        }

        // Closed, Back, or another preview meanwhile
        if ( request !== own ) return;
        request = undefined;
        resolving.value = undefined;

        if ( item && !failed ) {
            appStore.setSelectedContent( target.contentKey, item );
        } else {
            void router.replace( contentPageRoute( target ) ).catch( () => {} );
        }
    }

    // The page (a card, the bell, a dashboard row) opened or closed the slide-over through the store
    watch( shown, ( value: string | undefined, previous: string | undefined ): void => {
        // Read on every change, so a flag from a close that had no address never outlives it
        const closedWithoutAddress: boolean = appStore.takeContentClosedWithoutAddress();
        if ( value === addressed.value ) return;
        // boot-auth-1: a session ending closed it. Its caller navigates next (sign-in, home, the handoff's landing page),
        // replacing the address; Back from here would land after that navigation starts and undo it.
        if ( !value && closedWithoutAddress ) return;

        if ( value ) {
            cancelResolve();
            // A second item over the first keeps the one entry; the first opening pushes it, so Back closes it
            const location = withContentPreview( route, value );
            if ( addressed.value ) {
                void router.replace( isOverlayHistoryEntry( router ) ? { ...location, state: overlayHistoryState() } : location ).catch( () => {} );
            } else {
                void router.push( { ...location, state: overlayHistoryState() } ).catch( () => {} );
            }
        } else if ( addressed.value === previous && !resolving.value ) {
            // Closed on this page. A link to another page's preview has already replaced the address with its own.
            void leaveOverlayHistoryEntry( router, withContentPreview( route, undefined ) );
        }
    } );

    // The address changed: Back or Forward, a shared or refreshed ?preview= link, or a link to another page
    watch( addressed, ( value: string | undefined ): void => {
        // Whatever was resolving is no longer asked for (Back during a cold link's request)
        cancelResolve();
        // Our own push or replace landing
        if ( value === shown.value ) return;

        if ( !value ) {
            if ( appStore.contentModalVisible ) {
                appStore.clearSelectedContent();
            }
            return;
        }

        void openFromAddress( value );
    }, { immediate: true } );

    onScopeDispose( cancelResolve );

    function close(): void {
        const wasResolving: boolean = !!resolving.value;
        cancelResolve();

        if ( appStore.contentModalVisible ) {
            // The `shown` watcher takes the address back
            appStore.clearSelectedContent();
        } else if ( wasResolving && addressed.value ) {
            void leaveOverlayHistoryEntry( router, withContentPreview( route, undefined ) );
        }
    }

    const drawerVisible = computed< boolean >( {
        get: () => appStore.contentModalVisible || !!resolving.value,
        set: ( visible: boolean ) => {
            if ( !visible ) close();
        }
    } );

    return {
        drawerVisible,
        isResolving: computed< boolean >( () => !!resolving.value )
    };
}
